import db from "../config/firebase.config";
import firebase from "firebase/compat/app";

const COLL = "communities";

export const CATEGORIES = [
    "Academic",
    "Technology",
    "Business",
    "Sports",
    "Entertainment",
    "Gaming",
    "Photography",
    "Research",
    "Health",
    "Campus Life",
    "Politics",
    "Religion",
    "Career",
    "General",
];

export const TAG_SUGGESTIONS = [
    "Programming",
    "React",
    "Python",
    "GIS",
    "Football",
    "Machine Learning",
    "AI",
    "Photography",
    "Startups",
    "Design",
];

export const slugify = (str) =>
    str
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

export const subscribeCommunities = (cb) =>
    db
        .collection(COLL)
        .where("deleted", "==", false)
        .onSnapshot(
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                list.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
                cb(list);
            },
            () => cb([])
        );

export const subscribeCommunity = (slug, cb) =>
    db
        .collection(COLL)
        .where("slug", "==", slug)
        .limit(1)
        .onSnapshot(
            (snap) => {
                let found = null;
                snap.forEach((d) => (found = { id: d.id, ...d.data() }));
                cb(found);
            },
            () => cb(null)
        );

export const isCommunitySlugAvailable = async (slug) => {
    if (!slug) return false;
    const r = await db.collection(COLL).where("slug", "==", slug).get();
    return r.empty;
};

export const createCommunity = async (data, user) => {
    const ref = db.collection(COLL).doc();
    const now = firebase.firestore.FieldValue.serverTimestamp();
    await ref.set({
        id: ref.id,
        name: data.name,
        slug: data.slug,
        description: data.description || "",
        category: data.category || "General",
        visibility: data.visibility || "public",
        tags: data.tags || [],
        avatar: data.avatar || "",
        banner: data.banner || "",
        rules: data.rules || [],
        founderUid: user?.uid || "",
        founderName: user?.displayName || "A Fellow",
        admins: user?.uid ? [user.uid] : [],
        moderators: [],
        members: user?.uid ? [user.uid] : [],
        memberCount: user?.uid ? 1 : 0,
        joinRequests: [],
        bannedUids: [],
        mutedUids: [],
        deleted: false,
        createdAt: now,
        lastActivityAt: now,
    });
    if (user?.uid) {
        await ensureMemberProfile(ref.id, user);
    }
    return ref.id;
};

const memberProfileRef = (commId, uid) =>
    db.collection(COLL).doc(commId).collection("members").doc(uid);

export const ensureMemberProfile = async (commId, user) => {
    if (!user?.uid) return;
    const ref = memberProfileRef(commId, user.uid);
    const doc = await ref.get();
    if (!doc.exists) {
        await ref.set({
            uid: user.uid,
            name: user.displayName || user.email || "A Fellow",
            img: user.photoURL || "",
            joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    }
};

export const subscribeCommunityMembers = (commId, cb) =>
    db
        .collection(COLL)
        .doc(commId)
        .collection("members")
        .orderBy("joinedAt", "asc")
        .onSnapshot(
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                cb(list);
            },
            () => cb([])
        );

export const toggleMembership = async (commId, user) => {
    if (!user) return;
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const members = data.members || [];
    const next = members.includes(user.uid)
        ? members.filter((u) => u !== user.uid)
        : [...members, user.uid];
    await ref.update({ members: next, memberCount: next.length });
    if (next.includes(user.uid)) {
        await ensureMemberProfile(commId, user);
        if (data.founderUid && data.founderUid !== user.uid) {
            pushNotification(data.founderUid, {
                type: "join",
                fromUid: user.uid,
                fromName: user.displayName || user.email || "Someone",
                fromImg: user.photoURL || "",
                communityId: commId,
                communitySlug: data.slug,
                communityName: data.name,
                message: `${user.displayName || "Someone"} joined ${data.name}.`,
            });
        }
    } else {
        await memberProfileRef(commId, user.uid).delete();
    }
};

export const requestJoin = async (commId, user) => {
    if (!user) return;
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const requests = data.joinRequests || [];
    if (!requests.some((r) => r.uid === user.uid)) {
        await ref.update({
            joinRequests: [
                ...requests,
                {
                    uid: user.uid,
                    name: user.displayName || user.email || "A Fellow",
                    img: user.photoURL || "",
                    at: Date.now(),
                },
            ],
        });
        notifyAdmins(data, {
            type: "join_request",
            fromUid: user.uid,
            fromName: user.displayName || user.email || "A Fellow",
            fromImg: user.photoURL || "",
            communityId: commId,
            communitySlug: data.slug,
            communityName: data.name,
            message: `${user.displayName || "Someone"} requested to join ${data.name}.`,
        });
    }
};

export const leaveCommunity = async (commId, user) => {
    if (!user) return;
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const members = (data.members || []).filter((u) => u !== user.uid);
    const requests = (data.joinRequests || []).filter((r) => r.uid !== user.uid);
    await memberProfileRef(commId, user.uid).delete().catch(() => {});
    await ref.update({ members, memberCount: members.length, joinRequests: requests });
};

export const decideRequest = async (commId, request, approve) => {
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const requests = (data.joinRequests || []).filter((r) => r.uid !== request.uid);
    const patch = { joinRequests: requests };
    if (approve) {
        const members = data.members || [];
        if (!members.includes(request.uid)) members.push(request.uid);
        patch.members = members;
        patch.memberCount = members.length;
        await memberProfileRef(commId, request.uid)
            .set({
                uid: request.uid,
                name: request.name,
                img: request.img || "",
                joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .catch(() => {});
        pushNotification(request.uid, {
            type: "approved",
            fromUid: data.founderUid || "",
            fromName: request.name,
            communityId: commId,
            communitySlug: data.slug,
            communityName: data.name,
            message: `Your request to join ${data.name} was approved.`,
        });
    }
    await ref.update(patch);
};

// ─── Community Posts ───
export const subscribeCommunityPosts = (commId, cb) =>
    db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .orderBy("createdAt", "desc")
        .onSnapshot(
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                cb(list);
            },
            () => cb([])
        );

export const createCommunityPost = async (commId, { title, body, image }, user, community) => {
    const ref = await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .add({
            title,
            body,
            image: image || "",
            authorName: user?.displayName || user?.email || "Fellow",
            authorImg: user?.photoURL || "",
            authorUid: user?.uid || "",
            authorEmail: user?.email || "",
            communitySlug: community?.slug || "",
            communityName: community?.name || "",
            up: 0,
            down: 0,
            upvoters: [],
            downvoters: [],
            commentCount: 0,
            pinned: false,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            lastActivityAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    if (community && user) {
        (community.members || [])
            .filter((uid) => uid !== user.uid)
            .slice(0, 25)
            .forEach((uid) =>
                pushNotification(uid, {
                    type: "post",
                    fromUid: user.uid,
                    fromName: user.displayName || user.email || "A Fellow",
                    fromImg: user.photoURL || "",
                    communityId: commId,
                    communitySlug: community.slug,
                    communityName: community.name,
                    postId: ref.id,
                    message: `${user.displayName || "Someone"} posted in ${community.name}.`,
                })
            );
    }
    return ref;
};

export const voteCommunityPost = async (commId, postId, user, type) => {
    if (!user) return;
    const ref = db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const d = doc.data();
    const upvoters = d.upvoters || [];
    const downvoters = d.downvoters || [];
    let nextUp = upvoters;
    let nextDown = downvoters;

    if (type === "up") {
        if (upvoters.includes(user.uid)) nextUp = upvoters.filter((u) => u !== user.uid);
        else {
            nextUp = [...upvoters, user.uid];
            nextDown = downvoters.filter((u) => u !== user.uid);
        }
    } else {
        if (downvoters.includes(user.uid)) nextDown = downvoters.filter((u) => u !== user.uid);
        else {
            nextDown = [...downvoters, user.uid];
            nextUp = upvoters.filter((u) => u !== user.uid);
        }
    }
    await ref.update({
        up: nextUp.length,
        down: nextDown.length,
        upvoters: nextUp,
        downvoters: nextDown,
    });
};

export const subscribePostComments = (commId, postId, cb) =>
    db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .collection("comments")
        .orderBy("createdAt", "asc")
        .onSnapshot(
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                cb(list);
            },
            () => cb([])
        );

export const addPostComment = async (commId, postId, text, user) => {
    await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .collection("comments")
        .add({
            text,
            authorName: user?.displayName || user?.email || "Fellow",
            authorImg: user?.photoURL || "",
            authorUid: user?.uid || "",
            authorEmail: user?.email || "",
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    const postRef = db.collection(COLL).doc(commId).collection("posts").doc(postId);
    const doc = await postRef.get();
    if (doc.exists) {
        const pd = doc.data();
        await postRef.update({ commentCount: (pd.commentCount || 0) + 1 });
        if (pd.authorUid && pd.authorUid !== user?.uid) {
            pushNotification(pd.authorUid, {
                type: "comment",
                fromUid: user?.uid || "",
                fromName: user?.displayName || "A Fellow",
                fromImg: user?.photoURL || "",
                communityId: commId,
                communitySlug: pd.communitySlug,
                communityName: pd.communityName || "a community",
                postId,
                message: `${user?.displayName || "Someone"} commented on your post.`,
            });
        }
    }
};

// ─── Post edit / delete / pin ───
export const editCommunityPost = async (commId, postId, { title, body, image }) => {
    await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .update({
            title: title ?? "",
            body: body ?? "",
            image: image ?? "",
            editedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
};

export const deleteCommunityPost = async (commId, postId) => {
    const comments = await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .collection("comments")
        .get();
    const batch = db.batch();
    comments.forEach((c) =>
        batch.delete(
            db.collection(COLL).doc(commId).collection("posts").doc(postId).collection("comments").doc(c.id)
        )
    );
    batch.delete(db.collection(COLL).doc(commId).collection("posts").doc(postId));
    await batch.commit();
};

export const togglePinPost = async (commId, postId, pinned) => {
    await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .update({ pinned: !!pinned });
};

// ─── Comment edit / delete ───
export const editCommunityComment = async (commId, postId, commentId, text) => {
    await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .collection("comments")
        .doc(commentId)
        .update({
            text,
            editedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
};

export const deleteCommunityComment = async (commId, postId, commentId) => {
    await db
        .collection(COLL)
        .doc(commId)
        .collection("posts")
        .doc(postId)
        .collection("comments")
        .doc(commentId)
        .delete();
    const postRef = db.collection(COLL).doc(commId).collection("posts").doc(postId);
    const doc = await postRef.get();
    if (doc.exists) {
        await postRef.update({ commentCount: Math.max(0, (doc.data().commentCount || 0) - 1) });
    }
};

// ─── Moderation ───
export const removeMember = async (commId, member) => {
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const members = (data.members || []).filter((u) => u !== member.uid);
    await memberProfileRef(commId, member.uid).delete().catch(() => {});
    await ref.update({ members, memberCount: members.length });
    pushNotification(member.uid, {
        type: "removed",
        fromUid: data.founderUid || "",
        communityId: commId,
        communitySlug: data.slug,
        communityName: data.name,
        message: `You were removed from ${data.name}.`,
    });
};

export const banMember = async (commId, member) => {
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const members = (data.members || []).filter((u) => u !== member.uid);
    const banned = (data.bannedUids || []).includes(member.uid)
        ? data.bannedUids
        : [...(data.bannedUids || []), member.uid];
    await memberProfileRef(commId, member.uid).delete().catch(() => {});
    await ref.update({ members, memberCount: members.length, bannedUids: banned });
    pushNotification(member.uid, {
        type: "banned",
        fromUid: data.founderUid || "",
        communityId: commId,
        communitySlug: data.slug,
        communityName: data.name,
        message: `You were banned from ${data.name}.`,
    });
};

export const unbanMember = async (commId, uid) => {
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const banned = (doc.data().bannedUids || []).filter((u) => u !== uid);
    await ref.update({ bannedUids: banned });
};

export const transferOwnership = async (commId, member, user) => {
    const ref = db.collection(COLL).doc(commId);
    const doc = await ref.get();
    if (!doc.exists) return;
    const data = doc.data();
    const admins = [member.uid, ...(data.admins || [])].filter(
        (a, i, arr) => a && arr.indexOf(a) === i
    );
    await ref.update({ founderUid: member.uid, founderName: member.name, admins });
    pushNotification(member.uid, {
        type: "owner",
        fromUid: user?.uid || "",
        fromName: user?.displayName || "A Fellow",
        communityId: commId,
        communitySlug: data.slug,
        communityName: data.name,
        message: `You are now the owner of ${data.name}.`,
    });
};

// ─── Notifications ───
export const pushNotification = (uid, notif) => {
    if (!uid) return;
    const fromUid = notif.fromUid || "";
    db.collection("users")
        .doc(uid)
        .collection("notifications")
        .add({
            type: notif.type || "info",
            fromUid,
            fromName: notif.fromName || "",
            fromImg: notif.fromImg || "",
            communityId: notif.communityId || "",
            communitySlug: notif.communitySlug || "",
            communityName: notif.communityName || "",
            postId: notif.postId || "",
            message: notif.message || "",
            read: false,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        })
        .catch(() => {});
};

const notifyAdmins = (community, notif) => {
    const admins = [community.founderUid, ...(community.admins || [])].filter(
        (a, i, arr) => a && arr.indexOf(a) === i
    );
    admins.forEach((uid) => {
        if (uid !== notif.fromUid) pushNotification(uid, notif);
    });
};

export const subscribeNotifications = (uid, cb) => {
    if (!uid) return () => {};
    return db
        .collection("users")
        .doc(uid)
        .collection("notifications")
        .orderBy("createdAt", "desc")
        .limit(60)
        .onSnapshot(
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                cb(list);
            },
            () => cb([])
        );
};

export const markNotificationsRead = async (uid) => {
    if (!uid) return;
    const snap = await db
        .collection("users")
        .doc(uid)
        .collection("notifications")
        .where("read", "==", false)
        .get();
    const batch = db.batch();
    snap.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
};

export const markNotificationRead = async (uid, notifId) => {
    if (!uid || !notifId) return;
    await db
        .collection("users")
        .doc(uid)
        .collection("notifications")
        .doc(notifId)
        .update({ read: true })
        .catch(() => {});
};
